import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const statsRef = useRef<HTMLDivElement>(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const [execVisible, setExecVisible] = useState(false);
  const execRef = useRef<HTMLDivElement>(null);
  const [howItWorksVisible, setHowItWorksVisible] = useState(false);
  const howItWorksRef = useRef<HTMLDivElement>(null);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const location = useLocation();

  // Smooth scroll to hash section on page load or hash change
  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      const scrollToElement = () => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return true;
        }
        return false;
      };
      // Try immediately, then retry after a short delay for elements that may not be rendered yet
      if (!scrollToElement()) {
        const timer = setTimeout(() => {
          scrollToElement();
        }, 300);
        return () => clearTimeout(timer);
      }
    }
  }, [location.hash]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setExecVisible(true); },
      { threshold: 0.15 }
    );
    if (execRef.current) observer.observe(execRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setHowItWorksVisible(true); },
      { threshold: 0.1 }
    );
    if (howItWorksRef.current) observer.observe(howItWorksRef.current);
    return () => observer.disconnect();
  }, []);

  const testimonials = [
    {
      quote: "Full Circle Assist has transformed how I manage my life. They handle everything with such precision that I can focus entirely on growing my business.",
      name: "Alexandra M.",
      title: "CEO, Venture Capital",
    },
    {
      quote: "The level of service is extraordinary. From managing our household staff to coordinating international travel, every detail is handled flawlessly.",
      name: "Michael C.",
      title: "Founder, Private Equity",
    },
    {
      quote: "I never realized how much mental energy I was spending on logistics until Full Circle Assist took over. They are simply exceptional.",
      name: "Sarah W.",
      title: "Board Member, Philanthropy",
    },
    {
      quote: "Their executive support feels like having a seasoned chief of staff. Calendar management, vendor coordination, travel — all handled before I even think to ask.",
      name: "Jonathan H.",
      title: "Managing Partner, Legal Services",
    },
    {
      quote: "We entrusted them with our entire household operations across two properties. The seamless coordination and attention to detail has been remarkable.",
      name: "Catherine & David L.",
      title: "Private Family Office",
    },
    {
      quote: "As a surgeon with unpredictable hours, having Full Circle Assist manage my personal affairs has given me back something priceless — peace of mind.",
      name: "Dr. Priya N.",
      title: "Surgeon, Healthcare",
    },
    {
      quote: "They planned our anniversary trip to the Amalfi Coast down to every last detail. It was the most stress-free vacation we have ever taken.",
      name: "Robert & Elena V.",
      title: "Entrepreneurs",
    },
    {
      quote: "I run three companies simultaneously. Full Circle Assist keeps my operations synchronized across all of them. I genuinely could not function at this level without them.",
      name: "Marcus T.",
      title: "Serial Entrepreneur & Investor",
    },
    {
      quote: "The onboarding process was incredibly thorough. Within a week, my assistant understood my preferences better than people who have worked with me for years.",
      name: "Diana K.",
      title: "VP of Strategy, Corporate",
    },
    {
      quote: "From coordinating our children\u2019s school schedules to managing home renovations, they handle our family logistics with grace and professionalism.",
      name: "James & Olivia W.",
      title: "Private Family",
    },
    {
      quote: "Their discretion is unmatched. In my line of work, confidentiality is everything, and Full Circle Assist has never once given me reason to worry.",
      name: "Richard B.",
      title: "Senior Partner, Legal",
    },
    {
      quote: "I was skeptical about outsourcing personal tasks, but the quality of service converted me immediately. They anticipate needs I haven\u2019t even articulated yet.",
      name: "Sophia A.",
      title: "Creative Director, Design",
    },
    {
      quote: "Managing a portfolio of rental properties was consuming my weekends. Full Circle Assist took over vendor coordination and maintenance scheduling — I have my life back.",
      name: "Thomas R.",
      title: "Real Estate Investor",
    },
    {
      quote: "They coordinated a 200-person corporate retreat across three venues with zero hiccups. The event planning team is world-class.",
      name: "Natalie P.",
      title: "COO, Technology",
    },
    {
      quote: "What sets them apart is the strategic thinking. They don\u2019t just execute tasks — they optimize processes and suggest improvements I hadn\u2019t considered.",
      name: "Andrew C.",
      title: "Founder, Business Advisory",
    },
    {
      quote: "After my husband passed, managing our estate felt overwhelming. Full Circle Assist stepped in with compassion and competence during the most difficult time of my life.",
      name: "Margaret H.",
      title: "Private Client",
    },
    {
      quote: "I travel 200 days a year. They manage my entire itinerary, from private flights to restaurant reservations in cities I\u2019ve never visited. Flawless every time.",
      name: "Daniel O.",
      title: "International Business Consultant",
    },
    {
      quote: "The household management service has transformed our home life. Everything runs like clockwork — groceries, maintenance, staff coordination, all handled beautifully.",
      name: "Lauren & Christopher H.",
      title: "Private Family",
    },
    {
      quote: "I recommended Full Circle Assist to my entire executive team. Three of them signed up within a month. That should tell you everything about the quality.",
      name: "Victoria S.",
      title: "CEO, Media",
    },
    {
      quote: "Their bookkeeping and expense tracking support saved us from hiring a full-time finance coordinator. The ROI has been extraordinary.",
      name: "Kevin M.",
      title: "Founder, Venture Capital",
    },
    {
      quote: "From wellness appointments to personal shopping, they handle the details of my life so I can be fully present for my family and my patients.",
      name: "Dr. Amara J.",
      title: "Pediatric Specialist & Author",
    },
  ];

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    setFormStatus('sending');
    fetch('https://readdy.ai/api/form/d69vafua728k8ctu2v00', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData as any).toString(),
    })
      .then(res => {
        if (res.ok) { setFormStatus('success'); form.reset(); }
        else setFormStatus('error');
      })
      .catch(() => setFormStatus('error'));
  };

  const handleNewsletter = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    setNewsletterStatus('sending');
    fetch('https://readdy.ai/api/form/d69vah6a728k8ctu2v0g', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData as any).toString(),
    })
      .then(res => {
        if (res.ok) { setNewsletterStatus('success'); form.reset(); }
        else setNewsletterStatus('error');
      })
      .catch(() => setNewsletterStatus('error'));
  };

  const faqData = [
    {
      question: 'What services do you provide?',
      answer: 'We provide high-end virtual assistant support across three core areas: <strong>Business Operations</strong> — executive support, scheduling, project coordination, research, workflow management; <strong>Personal Affairs Management</strong> — lifestyle coordination, travel planning, wellness support, concierge services; <strong>Household Management</strong> — family logistics, home operations, vendor coordination, and household administration. All services are customized to your needs and designed to simplify your day-to-day life.',
    },
    {
      question: 'Who is your service designed for?',
      answer: 'Our services are ideal for busy professionals, executives, entrepreneurs, and families who value efficiency, discretion, and premium support. Clients typically seek to reclaim time, reduce complexity, and ensure their business and personal lives run seamlessly.',
    },
    {
      question: 'How does onboarding work?',
      answer: 'Once you select your services, we conduct a strategic onboarding session via Microsoft Teams or Zoom. During this session we establish your priorities, workflows, communication preferences, and service plan to ensure a smooth and efficient partnership from day one.',
    },
    {
      question: 'Do I get a dedicated assistant?',
      answer: 'Yes. You are paired with a dedicated assistant who understands your preferences, priorities, and processes. This ensures consistency, personalization, and proactive support.',
    },
    {
      question: 'How do you communicate with clients?',
      answer: 'We tailor communication to your preferences. This may include email, messaging platforms, scheduled check-ins, or priority request channels. Our approach is responsive, discreet, and designed around your schedule.',
    },
    {
      question: 'How quickly can you start?',
      answer: 'Following your consultation and service selection, onboarding is typically scheduled promptly so support can begin as quickly as possible.',
    },
    {
      question: 'Are services customizable?',
      answer: 'Absolutely. Every client receives a tailored service plan. Your support can scale up or down as your needs evolve, and services may be adjusted at any time.',
    },
    {
      question: 'How do you handle privacy and confidentiality?',
      answer: 'We maintain strict confidentiality standards and secure workflows. Your personal, household, and business information is handled with the highest level of discretion and professionalism.',
    },
    {
      question: 'What is your pricing structure?',
      answer: 'We offer premium service packages designed for ongoing support. Pricing varies based on service scope, complexity, and level of engagement. Full details are reviewed during your consultation.',
    },
    {
      question: 'Can you support both business and personal needs simultaneously?',
      answer: 'Yes — many clients choose integrated support across business operations, personal affairs, and household management for a fully streamlined experience.',
    },
    {
      question: 'What tasks do you not handle?',
      answer: 'While we manage a wide range of operational and lifestyle needs, we do not provide licensed professional services (legal, medical, financial advisory) but can help coordinate and manage those providers on your behalf.',
    },
    {
      question: 'What makes your team different from other assistant services?',
      answer: 'Our leadership team brings real C-suite executive experience from nationally recognized corporations, global data center buildout management, and utility infrastructure project oversight. This is not just task execution — it is strategic partnership from professionals who have operated at the executive level.',
    },
    {
      question: 'Do you work with clients outside the United States?',
      answer: 'Yes. We support clients globally and are experienced in coordinating across multiple time zones, international travel, and diverse operational requirements.',
    },
    {
      question: 'How do I get started?',
      answer: 'Simply connect with us to schedule a private consultation. We will discuss your needs, design your service plan, and guide you through onboarding.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7] font-sans text-[#2D2A26]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-[#FDFBF7]/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="w-full px-8 lg:px-16 py-5 flex items-center justify-between">
          <a href="#" className="flex items-center gap-2 cursor-pointer">
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="36" height="36" rx="18" fill="#C8A97E"/>
              <text x="18" y="14" textAnchor="middle" fill="white" fontSize="8" fontFamily="serif" fontWeight="600">F</text>
              <text x="18" y="21" textAnchor="middle" fill="white" fontSize="8" fontFamily="serif" fontWeight="600">C</text>
              <text x="18" y="28" textAnchor="middle" fill="white" fontSize="8" fontFamily="serif" fontWeight="600">A</text>
            </svg>
            <h1 className={`text-xl font-serif tracking-wide transition-colors duration-300 ${scrolled ? 'text-[#2D2A26]' : 'text-white'}`}>
              Full Circle <span className="text-[#C8A97E]">Assist</span>
            </h1>
          </a>
          <div className="hidden md:flex items-center gap-10">
            {['Services', 'How It Works', 'Testimonials', 'FAQ', 'Contact'].map(item => (
              <a
                key={item}
                href={`#${item.toLowerCase().replace(/\s+/g, '-')}`}
                className={`text-sm font-medium tracking-wide transition-colors duration-300 cursor-pointer hover:text-[#C8A97E] ${scrolled ? 'text-[#5A5650]' : 'text-white/80'}`}
              >
                {item}
              </a>
            ))}
            <Link
              to="/about"
              className={`text-sm font-medium tracking-wide transition-colors duration-300 cursor-pointer hover:text-[#C8A97E] ${scrolled ? 'text-[#5A5650]' : 'text-white/80'}`}
            >
              About
            </Link>
            <Link
              to="/logo-preview"
              className={`text-sm font-medium tracking-wide transition-colors duration-300 cursor-pointer hover:text-[#C8A97E] ${scrolled ? 'text-[#C8A97E]' : 'text-[#C8A97E]'}`}
            >
              Logo Preview
            </Link>
            <a href="#contact" className="px-6 py-2.5 bg-[#C8A97E] text-white text-sm font-medium rounded-md hover:bg-[#B89A6F] transition-colors cursor-pointer whitespace-nowrap">
              Get Started
            </a>
          </div>
          <button onClick={() => setMobileMenu(!mobileMenu)} className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${scrolled ? 'text-[#2D2A26]' : 'text-white'}`}>
            <i className={`ri-${mobileMenu ? 'close' : 'menu'}-line text-2xl`}></i>
          </button>
        </div>
        {mobileMenu && (
          <div className="md:hidden bg-[#FDFBF7] border-t border-[#E8E2D9] px-8 py-6 space-y-4">
            {['Services', 'How It Works', 'Testimonials', 'FAQ', 'Contact'].map(item => (
              <a key={item} href={`#${item.toLowerCase().replace(/\s+/g, '-')}`} onClick={() => setMobileMenu(false)} className="block text-sm font-medium text-[#5A5650] hover:text-[#C8A97E] cursor-pointer">{item}</a>
            ))}
            <Link to="/about" onClick={() => setMobileMenu(false)} className="block text-sm font-medium text-[#5A5650] hover:text-[#C8A97E] cursor-pointer">About</Link>
            <Link to="/logo-preview" onClick={() => setMobileMenu(false)} className="block text-sm font-medium text-[#C8A97E] hover:text-[#B89A6F] cursor-pointer">Logo Preview</Link>
          </div>
        )}
      </nav>

      {/* Hero Section — Full-bleed cinematic */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/7e791342ef0f3457dc7eca86ba879b6e.jpg"
            alt="Premium lifestyle"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/20"></div>
        </div>
        <div className="relative z-10 w-full px-8 lg:px-16 pt-32 pb-20">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-px bg-[#C8A97E]"></div>
              <span className="text-[#C8A97E] text-xs font-medium tracking-[0.3em] uppercase">Premium Life Management</span>
            </div>
            <h2 className="font-serif text-5xl lg:text-7xl text-white leading-[1.1] mb-8">
              Your Life,<br />Orchestrated to<br /><em className="text-[#C8A97E]">Perfection</em>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-12 max-w-lg">
              We manage the complexity of business, personal, and household operations so high-performing individuals and families can focus on what matters most.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <a href="#contact" className="px-8 py-4 bg-[#C8A97E] text-white text-sm font-medium rounded-md hover:bg-[#B89A6F] transition-all duration-300 cursor-pointer whitespace-nowrap">
                Schedule a Consultation
              </a>
              <a href="#services" className="px-8 py-4 border border-white/30 text-white text-sm font-medium rounded-md hover:border-[#C8A97E] hover:text-[#C8A97E] transition-all duration-300 cursor-pointer whitespace-nowrap">
                Explore Services
              </a>
            </div>
          </div>
        </div>
        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <span className="text-white/40 text-xs tracking-widest">SCROLL</span>
          <div className="w-px h-8 bg-gradient-to-b from-white/40 to-transparent"></div>
        </div>
      </section>

      {/* Stats Bar */}
      <section ref={statsRef} className="bg-[#2D2A26] py-16 px-8 lg:px-16">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { number: '150+', label: 'Families Served' },
            { number: '12+', label: 'Years of Excellence' },
            { number: '98%', label: 'Client Retention' },
            { number: '24/7', label: 'Availability' },
          ].map((stat, i) => (
            <div key={i} className={`transition-all duration-700 ${statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: `${i * 150}ms` }}>
              <p className="font-serif text-4xl lg:text-5xl text-[#C8A97E] mb-2">{stat.number}</p>
              <p className="text-white/60 text-sm tracking-wide">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Services Section — Horizontal cards */}
      <section id="services" className="py-28 px-8 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between mb-16">
            <div>
              <span className="text-[#C8A97E] text-xs font-medium tracking-[0.3em] uppercase mb-4 block">What We Do</span>
              <h2 className="font-serif text-4xl lg:text-5xl text-[#2D2A26]">Comprehensive<br />Life Infrastructure</h2>
            </div>
            <p className="hidden md:block text-[#7A756E] text-sm leading-relaxed max-w-sm text-right">
              Three pillars of support designed to eliminate friction from every dimension of your life.
            </p>
          </div>

          <div className="space-y-6">
            {[
              {
                num: '01',
                title: 'Business Operations',
                desc: 'Comprehensive business support spanning administrative tasks, communications, finance, operations, marketing, sales, and strategic executive assistance.',
                image: 'https://readdy.ai/api/search-image?query=elegant%20modern%20executive%20office%20with%20warm%20wood%20desk%2C%20premium%20leather%20accessories%2C%20golden%20hour%20sunlight%20streaming%20through%20large%20windows%2C%20warm%20amber%20and%20cream%20tones%2C%20minimalist%20luxury%20workspace%2C%20architectural%20interior%20photography%2C%20soft%20shadows&width=600&height=400&seq=svc-biz-v2&orientation=landscape',
                tags: [
                  'Administrative & Scheduling',
                  'Communication & Support',
                  'Finance & Bookkeeping',
                  'Operations & Project Management',
                  'Marketing Support',
                  'Sales & Business Development',
                  'Personal & Executive Support',
                  'High-Value Strategic Tasks'
                ],
              },
              {
                num: '02',
                title: 'Personal Affairs',
                desc: 'Lifestyle management, personal shopping, appointment scheduling, and dedicated assistance for all aspects of your personal life.',
                image: 'https://readdy.ai/api/search-image?query=luxury%20lifestyle%20flat%20lay%20with%20premium%20leather%20planner%2C%20gold%20pen%2C%20fresh%20white%20peonies%2C%20cashmere%20fabric%2C%20warm%20natural%20light%20on%20marble%20surface%2C%20warm%20earth%20tones%20and%20cream%20palette%2C%20editorial%20product%20photography%2C%20soft%20and%20elegant&width=600&height=400&seq=svc-personal-v2&orientation=landscape',
                tags: [
                  'Shopping & Personal Purchases',
                  'Lifestyle & Personal Scheduling',
                  'Wellness & Health Coordination',
                  'Travel & Concierge Services',
                  'Social & Personal Event Planning',
                  'Personal Administration',
                ],
              },
              {
                num: '03',
                title: 'Household Management',
                desc: 'Staff coordination, property oversight, maintenance scheduling, and complete household operations managed with meticulous attention.',
                image: 'https://readdy.ai/api/search-image?query=stunning%20luxury%20home%20living%20room%20interior%20with%20warm%20earth%20tone%20furnishings%2C%20natural%20linen%20sofa%2C%20terracotta%20accents%2C%20large%20windows%20with%20garden%20view%2C%20warm%20golden%20afternoon%20light%2C%20premium%20residential%20interior%20design%20photography%2C%20cozy%20and%20refined&width=600&height=400&seq=svc-house-v2&orientation=landscape',
                tags: [
                  'Home Operations & Maintenance',
                  'Family Management',
                  'Household Administration & Logistics',
                  'Household Purchasing & Supplies',
                ],
              },
            ].map((service, i) => (
              <div key={i} className="group grid grid-cols-1 md:grid-cols-2 gap-0 bg-white rounded-xl overflow-hidden border border-[#E8E2D9]/60 hover:shadow-lg transition-all duration-500 cursor-pointer">
                <div className={`relative h-64 md:h-80 overflow-hidden ${i % 2 === 1 ? 'md:order-2' : ''}`}>
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute top-5 left-5 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-md">
                    <span className="text-[#C8A97E] font-serif text-lg">{service.num}</span>
                  </div>
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <h3 className="font-serif text-3xl text-[#2D2A26] mb-4">{service.title}</h3>
                  <p className="text-[#7A756E] text-sm leading-relaxed mb-6">{service.desc}</p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {service.tags.map(tag => (
                      (i === 0 || i === 1 || i === 2) ? (
                        <a
                          key={tag}
                          href={i === 0 ? '#business-operations-detail' : i === 1 ? '#personal-affairs-detail' : '#household-management-detail'}
                          className="px-3 py-1 bg-[#C8A97E] text-white text-xs rounded-full hover:bg-[#B89A6F] transition-colors cursor-pointer whitespace-nowrap"
                        >
                          {tag} <i className="ri-arrow-right-s-line text-xs"></i>
                        </a>
                      ) : (
                        <span key={tag} className="px-3 py-1 bg-[#F5F0E8] text-[#8A7D6B] text-xs rounded-full">{tag}</span>
                      )
                    ))}
                  </div>
                  <a href="#contact" className="inline-flex items-center gap-2 text-[#C8A97E] text-sm font-medium hover:gap-3 transition-all cursor-pointer">
                    Learn More <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Operations Detail Section */}
      <section id="business-operations-detail" className="py-28 px-8 lg:px-16 bg-[#F5F0E8]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-10 h-px bg-[#C8A97E]"></div>
              <span className="text-[#C8A97E] text-xs font-medium tracking-[0.3em] uppercase">Business Operations</span>
              <div className="w-10 h-px bg-[#C8A97E]"></div>
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#2D2A26] mb-6">Complete Business<br />Support Ecosystem</h2>
            <p className="text-[#7A756E] text-sm leading-relaxed max-w-2xl mx-auto">
              From day-to-day administrative tasks to high-level strategic initiatives, we provide comprehensive support across every dimension of your business operations.
            </p>
          </div>

          <div className="space-y-8">
            {[
              {
                icon: 'ri-calendar-check-line',
                title: 'Administrative & Scheduling Tasks',
                tasks: [
                  'Calendar management and appointment scheduling',
                  'Meeting coordination and reminders',
                  'Travel booking (flights, hotels, itineraries)',
                  'Inbox organization and email filtering',
                  'Data entry and database management',
                  'Document formatting and organization',
                  'File management and cloud storage organization',
                  'Creating reports and presentations',
                  'Google Workspace and Microsoft Office management'
                ]
              },
              {
                icon: 'ri-mail-send-line',
                title: 'Communication & Customer Support',
                tasks: [
                  'Email responses and follow-ups',
                  'Customer service inquiries',
                  'Live chat support',
                  'CRM updates and management',
                  'Client onboarding coordination',
                  'Vendor communication',
                  'Internal team coordination via Slack',
                  'Meeting setup through Zoom'
                ]
              },
              {
                icon: 'ri-money-dollar-circle-line',
                title: 'Finance & Bookkeeping Support',
                tasks: [
                  'Invoice creation and tracking',
                  'Expense tracking and reporting',
                  'Accounts payable/receivable monitoring',
                  'Budget tracking',
                  'Basic bookkeeping support',
                  'Financial report preparation',
                  'Payroll coordination',
                  'QuickBooks and accounting software management'
                ]
              },
              {
                icon: 'ri-bar-chart-box-line',
                title: 'Operations & Project Management',
                tasks: [
                  'Project coordination and task tracking',
                  'SOP (standard operating procedure) documentation',
                  'Workflow creation and optimization',
                  'Deadline tracking',
                  'Research and vendor sourcing',
                  'Business process improvement support'
                ]
              },
              {
                icon: 'ri-megaphone-line',
                title: 'Marketing Support',
                tasks: [
                  'Social media scheduling and posting',
                  'Content calendar management',
                  'Blog formatting and publishing',
                  'Email marketing campaigns',
                  'Basic graphic creation',
                  'Market research and competitor analysis',
                  'Lead generation and list building'
                ]
              },
              {
                icon: 'ri-shopping-cart-line',
                title: 'Sales & Business Development',
                tasks: [
                  'Lead qualification and outreach',
                  'CRM management',
                  'Proposal preparation',
                  'Sales pipeline tracking',
                  'Appointment setting for sales teams',
                  'Client follow-up systems'
                ]
              },
              {
                icon: 'ri-user-star-line',
                title: 'Personal & Executive Support',
                tasks: [
                  'Personal scheduling and reminders',
                  'Household vendor coordination',
                  'Event planning',
                  'Online purchases and returns',
                  'Personal travel planning',
                  'Family calendar management',
                  'Concierge-style services'
                ]
              },
              {
                icon: 'ri-trophy-line',
                title: 'High-Value Strategic Tasks',
                tasks: [
                  'KPI tracking and dashboards',
                  'Hiring coordination and interview scheduling',
                  'Business research and analysis',
                  'Presentation preparation for executives',
                  'Process automation setup',
                  'Systems implementation'
                ]
              }
            ].map((category, i) => (
              <div key={i} className="bg-white rounded-xl p-8 lg:p-10 border border-[#E8E2D9]/60 hover:shadow-md transition-all duration-300">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-xl bg-[#C8A97E]/10 flex items-center justify-center">
                      <i className={`${category.icon} text-[#C8A97E] text-2xl`}></i>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif text-2xl text-[#2D2A26] mb-5">
                      <a href="#contact" className="hover:text-[#C8A97E] transition-colors cursor-pointer">{category.title}</a>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {category.tasks.map((task, j) => (
                        <div key={j} className="flex items-start gap-2.5">
                          <i className="ri-check-line text-[#C8A97E] text-sm mt-0.5 flex-shrink-0"></i>
                          <span className="text-[#7A756E] text-sm leading-relaxed">{task}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <div className="bg-[#2D2A26] rounded-2xl p-10 lg:p-14">
              <h3 className="font-serif text-3xl lg:text-4xl text-white mb-5">
                Ready to Streamline Your Operations?
              </h3>
              <p className="text-white/60 text-sm leading-relaxed mb-8 max-w-2xl mx-auto">
                Our team brings C-suite executive experience, global infrastructure project management, and decades of combined business expertise to every engagement. Let us handle the complexity so you can focus on growth.
              </p>
              <a href="#contact" className="inline-flex items-center gap-2 px-8 py-4 bg-[#C8A97E] text-white text-sm font-medium rounded-md hover:bg-[#B89A6F] transition-colors cursor-pointer whitespace-nowrap">
                Schedule a Consultation <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Personal Affairs Detail Section */}
      <section id="personal-affairs-detail" className="py-28 px-8 lg:px-16 bg-[#F5F0E8]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-10 h-px bg-[#C8A97E]"></div>
              <span className="text-[#C8A97E] text-xs font-medium tracking-[0.3em] uppercase">Personal Affairs</span>
              <div className="w-10 h-px bg-[#C8A97E]"></div>
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#2D2A26] mb-6">Complete Personal<br />Lifestyle Management</h2>
            <p className="text-[#7A756E] text-sm leading-relaxed max-w-2xl mx-auto">
              From daily errands and wellness coordination to luxury travel and personal event planning, we handle every detail of your personal life with care and precision.
            </p>
          </div>

          <div className="space-y-8">
            {[
              {
                icon: 'ri-shopping-bag-line',
                title: 'Shopping & Personal Purchases',
                tasks: [
                  'Online shopping and price comparisons',
                  'Gift sourcing and purchasing',
                  'Personal product research and recommendations',
                  'Subscription management',
                  'Wardrobe planning and clothing sourcing',
                  'Returns and exchanges handling',
                  'Personal grocery ordering and delivery coordination',
                  'Specialty item sourcing',
                  'Managed through platforms like Amazon and Instacart'
                ]
              },
              {
                icon: 'ri-calendar-todo-line',
                title: 'Lifestyle & Personal Scheduling',
                tasks: [
                  'Personal calendar management',
                  'Appointment scheduling (medical, beauty, fitness)',
                  'Restaurant reservations',
                  'Travel planning and itineraries',
                  'Local activity recommendations',
                  'Personal routine and time-blocking planning',
                  'Personal goal tracking and accountability'
                ]
              },
              {
                icon: 'ri-heart-pulse-line',
                title: 'Wellness & Health Coordination',
                tasks: [
                  'Medical appointment scheduling',
                  'Fitness class booking',
                  'Meal planning and nutrition coordination',
                  'Supplement tracking and ordering',
                  'Habit tracking and wellness check-ins',
                  'Meditation or fitness planning using services like Peloton or Headspace'
                ]
              },
              {
                icon: 'ri-plane-line',
                title: 'Travel & Concierge Services',
                tasks: [
                  'Vacation research and planning',
                  'Flight and hotel booking',
                  'Packing lists and travel preparation',
                  'Transportation coordination',
                  'Experience and excursion booking',
                  'Travel document reminders',
                  'Luxury travel planning'
                ]
              },
              {
                icon: 'ri-gift-line',
                title: 'Social & Personal Event Planning',
                tasks: [
                  'Personal event planning',
                  'Guest list management',
                  'Invitations and RSVPs',
                  'Birthday and milestone celebrations',
                  'Holiday planning and coordination',
                  'Experience and reservation sourcing'
                ]
              },
              {
                icon: 'ri-folder-user-line',
                title: 'Personal Administration',
                tasks: [
                  'Personal email management',
                  'Document organization and filing',
                  'Personal records management',
                  'Insurance and membership tracking',
                  'Digital life organization',
                  'Bill reminders and organization'
                ]
              }
            ].map((category, i) => (
              <div key={i} className="bg-white rounded-xl p-8 lg:p-10 border border-[#E8E2D9]/60 hover:shadow-md transition-all duration-300">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-xl bg-[#C8A97E]/10 flex items-center justify-center">
                      <i className={`${category.icon} text-[#C8A97E] text-2xl`}></i>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif text-2xl text-[#2D2A26] mb-5">
                      <a href="#contact" className="hover:text-[#C8A97E] transition-colors cursor-pointer">{category.title}</a>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {category.tasks.map((task, j) => (
                        <div key={j} className="flex items-start gap-2.5">
                          <i className="ri-check-line text-[#C8A97E] text-sm mt-0.5 flex-shrink-0"></i>
                          <span className="text-[#7A756E] text-sm leading-relaxed">{task}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <div className="bg-[#2D2A26] rounded-2xl p-10 lg:p-14">
              <h3 className="font-serif text-3xl lg:text-4xl text-white mb-5">
                Ready to Simplify Your Personal Life?
              </h3>
              <p className="text-white/60 text-sm leading-relaxed mb-8 max-w-2xl mx-auto">
                From shopping and wellness to travel and event planning, we take care of every personal detail so you can focus on what truly matters. Let us handle the logistics of your life.
              </p>
              <a href="#contact" className="inline-flex items-center gap-2 px-8 py-4 bg-[#C8A97E] text-white text-sm font-medium rounded-md hover:bg-[#B89A6F] transition-colors cursor-pointer whitespace-nowrap">
                Schedule a Consultation <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Household Management Detail Section */}
      <section id="household-management-detail" className="py-28 px-8 lg:px-16 bg-[#F5F0E8]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-10 h-px bg-[#C8A97E]"></div>
              <span className="text-[#C8A97E] text-xs font-medium tracking-[0.3em] uppercase">Household Management</span>
              <div className="w-10 h-px bg-[#C8A97E]"></div>
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#2D2A26] mb-6">Complete Household<br />Operations Management</h2>
            <p className="text-[#7A756E] text-sm leading-relaxed max-w-2xl mx-auto">
              From home maintenance and family coordination to household logistics and purchasing, we manage every detail of your household with care and precision.
            </p>
          </div>

          <div className="space-y-8">
            {[
              {
                icon: 'ri-home-gear-line',
                title: 'Home Operations & Maintenance',
                tasks: [
                  'Scheduling home maintenance and repairs',
                  'Managing household vendors (cleaning, landscaping, etc.)',
                  'Home service research and booking',
                  'Move coordination',
                  'Home organization projects',
                  'Smart home service coordination',
                  'Household inventory tracking'
                ]
              },
              {
                icon: 'ri-parent-line',
                title: 'Family Management',
                tasks: [
                  'Family calendar coordination',
                  'School schedules and reminders',
                  'Kids\u2019 activities and program registration',
                  'Camp and extracurricular research',
                  'Childcare coordination',
                  'Family travel planning',
                  'Family communication coordination'
                ]
              },
              {
                icon: 'ri-file-list-3-line',
                title: 'Household Administration & Logistics',
                tasks: [
                  'Household bill tracking and reminders',
                  'Managing household subscriptions and services',
                  'Vendor payments and service renewals',
                  'Household document organization',
                  'Family record management',
                  'Household budgeting support'
                ]
              },
              {
                icon: 'ri-shopping-basket-line',
                title: 'Household Purchasing & Supplies',
                tasks: [
                  'Household supply ordering',
                  'Home goods purchasing',
                  'Grocery and meal coordination for the household',
                  'Bulk purchase management',
                  'Seasonal and holiday household planning'
                ]
              }
            ].map((category, i) => (
              <div key={i} className="bg-white rounded-xl p-8 lg:p-10 border border-[#E8E2D9]/60 hover:shadow-md transition-all duration-300">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-xl bg-[#C8A97E]/10 flex items-center justify-center">
                      <i className={`${category.icon} text-[#C8A97E] text-2xl`}></i>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif text-2xl text-[#2D2A26] mb-5">
                      <a href="#contact" className="hover:text-[#C8A97E] transition-colors cursor-pointer">{category.title}</a>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {category.tasks.map((task, j) => (
                        <div key={j} className="flex items-start gap-2.5">
                          <i className="ri-check-line text-[#C8A97E] text-sm mt-0.5 flex-shrink-0"></i>
                          <span className="text-[#7A756E] text-sm leading-relaxed">{task}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <div className="bg-[#2D2A26] rounded-2xl p-10 lg:p-14">
              <h3 className="font-serif text-3xl lg:text-4xl text-white mb-5">
                Ready to Simplify Your Household?
              </h3>
              <p className="text-white/60 text-sm leading-relaxed mb-8 max-w-2xl mx-auto">
                From home operations and family coordination to purchasing and administration, we take care of every household detail so you can enjoy your home and family without the stress of managing it all.
              </p>
              <a href="#contact" className="inline-flex items-center gap-2 px-8 py-4 bg-[#C8A97E] text-white text-sm font-medium rounded-md hover:bg-[#B89A6F] transition-colors cursor-pointer whitespace-nowrap">
                Schedule a Consultation <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About / Process Section */}
      <section id="about" className="py-28 px-8 lg:px-16 bg-[#F5F0E8]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
            <div className="relative">
              <div className="rounded-xl overflow-hidden shadow-xl">
                <img
                  src="https://readdy.ai/api/search-image?query=two%20elegant%20professionals%20having%20a%20warm%20conversation%20in%20a%20refined%20private%20office%20with%20soft%20natural%20light%2C%20warm%20earth%20tones%2C%20cream%20and%20caramel%20color%20palette%2C%20luxury%20consultation%20meeting%2C%20candid%20professional%20photography%2C%20sophisticated%20and%20welcoming%20atmosphere&width=600&height=500&seq=about-v2&orientation=portrait"
                  alt="Consultation"
                  className="w-full h-[420px] object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#C8A97E] rounded-xl p-6 shadow-lg hidden lg:block">
                <p className="text-white font-serif text-3xl mb-1">12+</p>
                <p className="text-white/80 text-xs tracking-wide">Years of<br />Excellence</p>
              </div>
            </div>
            <div>
              <span className="text-[#C8A97E] text-xs font-medium tracking-[0.3em] uppercase mb-4 block">Our Approach</span>
              <h2 className="font-serif text-4xl lg:text-5xl text-[#2D2A26] mb-6 leading-tight">
                Built on Trust,<br />Delivered with Precision
              </h2>
              <p className="text-[#7A756E] text-sm leading-loose mb-8">
                We believe exceptional service begins with deeply understanding our clients. Every engagement starts with a comprehensive assessment of your life\u2019s moving parts \u2014 business commitments, personal priorities, household needs \u2014 so we can design a support system that works seamlessly behind the scenes.
              </p>
              <p className="text-[#7A756E] text-sm leading-loose mb-10">
                Our team of seasoned professionals brings decades of combined experience in executive assistance, estate management, and lifestyle coordination. We operate with the highest standards of <strong>discretion</strong> and <strong>confidentiality</strong>.
              </p>
              <a href="#contact" className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#2D2A26] text-white text-sm font-medium rounded-md hover:bg-[#3D3A36] transition-colors cursor-pointer whitespace-nowrap">
                Start a Conversation <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>

          {/* Process Steps */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: 'ri-chat-3-line', title: 'Discovery Call', desc: 'We learn about your unique needs, priorities, and lifestyle to craft a tailored plan.' },
              { icon: 'ri-file-list-3-line', title: 'Custom Blueprint', desc: 'A detailed service blueprint is designed around your specific requirements.' },
              { icon: 'ri-team-line', title: 'Team Assembly', desc: 'We assign a dedicated team of specialists matched to your profile.' },
              { icon: 'ri-shield-check-line', title: 'Seamless Execution', desc: 'Ongoing management with regular check-ins and continuous optimization.' },
            ].map((step, i) => (
              <div key={i} className="bg-white rounded-xl p-7 border border-[#E8E2D9]/60 hover:shadow-md transition-all duration-300 group">
                <div className="w-11 h-11 rounded-lg bg-[#C8A97E]/10 flex items-center justify-center mb-5 group-hover:bg-[#C8A97E]/20 transition-colors">
                  <i className={`${step.icon} text-[#C8A97E] text-xl`}></i>
                </div>
                <div className="text-[#C8A97E] font-serif text-xs mb-3">Step {i + 1}</div>
                <h4 className="font-serif text-lg text-[#2D2A26] mb-3">
                  <a href="#contact" className="hover:text-[#C8A97E] transition-colors cursor-pointer">{step.title}</a>
                </h4>
                <p className="text-[#7A756E] text-xs leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" ref={howItWorksRef} className="py-28 px-8 lg:px-16 bg-[#FDFBF7]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <span className="text-[#C8A97E] text-xs font-medium tracking-[0.3em] uppercase mb-4 block">How It Works</span>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#2D2A26] mb-6">A Refined, Effortless Experience<br />Designed Around You</h2>
            <p className="text-[#7A756E] text-sm leading-relaxed max-w-2xl mx-auto">
              Our process is intentionally simple, discreet, and personalized — delivering seamless support across your business operations, personal affairs, and household management with the precision of a private concierge.
            </p>
          </div>

          <div className="space-y-8">
            {[
              {
                icon: 'ri-hand-heart-line',
                step: '1',
                title: 'Connect With Us',
                subtitle: 'Your experience begins with a private introduction.',
                description: 'Reach out through our contact form or schedule a consultation at your convenience. We take time to understand your priorities, lifestyle, and operational needs — whether business, personal, or household.',
                highlight: 'This first step allows us to design a service experience tailored specifically to you.',
              },
              {
                icon: 'ri-phone-line',
                step: '2',
                title: 'Private Consultation',
                subtitle: 'We arrange a personalized phone consultation to explore how we can best support you.',
                description: 'During this conversation, we will understand your current demands and time pressures, identify opportunities to simplify and optimize your day-to-day life, introduce our full range of services, discuss service structure and investment options, and answer any questions with complete transparency.',
                highlight: 'Every recommendation is customized to your goals, preferences, and pace of life.',
              },
              {
                icon: 'ri-file-list-3-line',
                step: '3',
                title: 'Curated Service Selection',
                subtitle: 'Based on your consultation, we design a tailored support plan aligned with your needs.',
                description: 'Your personalized service portfolio may include Business Operations Management — executive support, coordination, and workflow optimization; Personal Affairs Management — lifestyle, travel, wellness, and concierge services; and Household Management — family logistics, home operations, and vendor coordination.',
                highlight: 'Your services remain flexible and evolve with you.',
              },
              {
                icon: 'ri-settings-3-line',
                step: '4',
                title: 'Strategic Onboarding & Alignment',
                subtitle: 'Once you begin, we host a comprehensive onboarding session via Microsoft Teams or Zoom to establish your personalized service framework.',
                description: 'This session is designed to ensure flawless execution from the start. We will clarify your priorities and expectations, develop your strategic support plan and service roadmap, establish communication and approval preferences, implement workflows and collaboration systems, and review confidentiality and security protocols. We leverage industry-leading platforms including Google Workspace and Slack for communication, Asana or ClickUp for task management, Notion for knowledge bases, 1Password for security, Google Drive or Dropbox for file storage, Zapier for automation, Stripe for billing, and HubSpot for CRM.',
                highlight: 'As a highly technical organization, we adapt seamlessly to your existing infrastructure and tools — ensuring a frictionless integration with your current systems.',
              },
              {
                icon: 'ri-customer-service-2-line',
                step: '5',
                title: 'Ongoing Concierge Support',
                subtitle: 'Your dedicated assistant begins managing, coordinating, and optimizing your priorities with discretion and precision.',
                description: 'You can expect proactive, attentive communication; thoughtful anticipation of needs; consistent updates and transparent progress; continuous refinement of systems and processes; and absolute respect for your time and privacy.',
                highlight: 'We handle the details — so you can focus on what matters most.',
              },
            ].map((item, i) => (
              <div
                key={i}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-start transition-all duration-700 ${howItWorksVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                <div className="lg:col-span-2 flex lg:flex-col items-center lg:items-start gap-4">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-xl bg-[#C8A97E]/10 flex items-center justify-center group-hover:bg-[#C8A97E]/20 transition-colors">
                      <i className={`${item.icon} text-[#C8A97E] text-2xl`}></i>
                    </div>
                    <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#C8A97E] flex items-center justify-center">
                      <span className="text-white font-serif text-sm">{item.step}</span>
                    </div>
                  </div>
                  {i < 4 && (
                    <div className="hidden lg:block w-px h-20 bg-gradient-to-b from-[#C8A97E]/40 to-transparent ml-8 mt-2"></div>
                  )}
                </div>

                <div className="lg:col-span-10 bg-white rounded-xl p-8 border border-[#E8E2D9]/60 hover:shadow-md transition-all duration-300">
                  <h3 className="font-serif text-2xl text-[#2D2A26] mb-2">
                    <a href="#contact" className="hover:text-[#C8A97E] transition-colors cursor-pointer">{item.title}</a>
                  </h3>
                  <p className="text-[#C8A97E] text-sm font-medium mb-4">{item.subtitle}</p>
                  <p className="text-[#7A756E] text-sm leading-relaxed mb-4">{item.description}</p>
                  <div className="flex items-start gap-2.5 bg-[#F5F0E8] rounded-lg p-4">
                    <i className="ri-star-line text-[#C8A97E] text-base mt-0.5 flex-shrink-0"></i>
                    <p className="text-[#5A5650] text-sm leading-relaxed italic">{item.highlight}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <a href="#contact" className="inline-flex items-center gap-2 px-8 py-4 bg-[#C8A97E] text-white text-sm font-medium rounded-md hover:bg-[#B89A6F] transition-colors cursor-pointer whitespace-nowrap">
              Begin Your Journey <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-28 px-8 lg:px-16 bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#C8A97E] text-xs font-medium tracking-[0.3em] uppercase mb-4 block">
              Frequently Asked Questions
            </span>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#2D2A26] mb-6">
              Everything You Need<br />to Know
            </h2>
            <p className="text-[#7A756E] text-sm leading-relaxed max-w-2xl mx-auto">
              Have questions about our services, process, or how we can support you? Find answers to the most common inquiries below.
            </p>
          </div>

          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border border-[#E8E2D9]/60 overflow-hidden hover:shadow-md transition-all duration-300"
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full px-8 py-6 flex items-center justify-between gap-4 text-left cursor-pointer group"
                >
                  <h3 className="font-serif text-lg text-[#2D2A26] group-hover:text-[#C8A97E] transition-colors">
                    {faq.question}
                  </h3>
                  <div className={`flex-shrink-0 w-8 h-8 rounded-lg bg-[#C8A97E]/10 flex items-center justify-center transition-all duration-300 ${openFaqIndex === index ? 'rotate-180 bg-[#C8A97E]' : ''}`}>
                    <i className={`ri-arrow-down-s-line text-lg ${openFaqIndex === index ? 'text-white' : 'text-[#C8A97E]'}`}></i>
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-500 ${openFaqIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div className="px-8 pb-6">
                    <div className="pt-2 border-t border-[#E8E2D9]/40">
                      <p
                        className="text-[#7A756E] text-sm leading-relaxed mt-4"
                        dangerouslySetInnerHTML={{ __html: faq.answer }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-[#7A756E] text-sm mb-6">
              Still have questions? We're here to help.
            </p>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#C8A97E] text-white text-sm font-medium rounded-md hover:bg-[#B89A6F] transition-colors cursor-pointer whitespace-nowrap"
            >
              Schedule a Consultation <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Detailed Services Grid */}
      <section className="py-28 px-8 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#C8A97E] text-xs font-medium tracking-[0.3em] uppercase mb-4 block">Specialized Solutions</span>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#2D2A26]">Tailored to Every Dimension</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: 'ri-plane-line',
                title: 'Travel Coordination',
                desc: 'Private jet bookings, luxury accommodations, and detailed itineraries crafted for seamless journeys.',
                image: 'https://readdy.ai/api/search-image?query=luxury%20first%20class%20airplane%20cabin%20interior%20with%20warm%20leather%20seats%20and%20golden%20ambient%20lighting%2C%20premium%20aviation%20travel%20experience%2C%20warm%20earth%20tones%2C%20elegant%20and%20sophisticated%2C%20cinematic%20photography&width=400&height=300&seq=detail-travel-v2&orientation=landscape',
              },
              {
                icon: 'ri-goblet-line',
                title: 'Event Planning',
                desc: 'Flawless execution of personal and professional events, from intimate dinners to grand celebrations.',
                image: 'https://readdy.ai/api/search-image?query=elegant%20dinner%20party%20table%20setting%20with%20warm%20candlelight%2C%20premium%20floral%20centerpiece%2C%20fine%20china%20and%20crystal%20glasses%2C%20warm%20amber%20and%20cream%20tones%2C%20luxury%20event%20styling%2C%20editorial%20food%20and%20lifestyle%20photography&width=400&height=300&seq=detail-event-v2&orientation=landscape',
              },
              {
                icon: 'ri-home-heart-line',
                title: 'Estate Management',
                desc: 'Comprehensive property oversight including maintenance, security, staff management, and seasonal preparations.',
                image: 'https://readdy.ai/api/search-image?query=beautiful%20luxury%20estate%20garden%20with%20manicured%20hedges%20and%20stone%20pathway%20leading%20to%20elegant%20villa%2C%20warm%20golden%20hour%20sunlight%2C%20earth%20tones%20and%20green%20foliage%2C%20premium%20residential%20property%20photography%2C%20serene%20and%20sophisticated&width=400&height=300&seq=detail-estate-v2&orientation=landscape',
              },
            ].map((item, i) => (
              <div key={i} className="group rounded-xl overflow-hidden bg-white border border-[#E8E2D9]/60 hover:shadow-lg transition-all duration-500 cursor-pointer">
                <div className="relative h-52 overflow-hidden">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 w-10 h-10 rounded-lg bg-white/90 backdrop-blur-sm flex items-center justify-center">
                    <i className={`${item.icon} text-[#C8A97E] text-lg`}></i>
                  </div>
                </div>
                <div className="p-6">
                  <h4 className="font-serif text-xl text-[#2D2A26] mb-3">
                    <a href="#contact" className="hover:text-[#C8A97E] transition-colors cursor-pointer">{item.title}</a>
                  </h4>
                  <p className="text-[#7A756E] text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Executive Support Section */}
      <section id="executive-support" ref={execRef} className="py-28 px-8 lg:px-16 bg-[#FDFBF7]">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-px bg-[#C8A97E]"></div>
                <span className="text-[#C8A97E] text-xs font-medium tracking-[0.3em] uppercase">Executive Support</span>
                <div className="w-10 h-px bg-[#C8A97E]"></div>
              </div>
              <h2 className="font-serif text-4xl lg:text-5xl text-[#2D2A26] mb-6 leading-tight">
                C-Suite Caliber<br />Support, On Demand
              </h2>
              <p className="text-[#7A756E] text-sm leading-loose mb-6">
                Our executive support services are built on real-world leadership experience at the highest levels. Our team brings direct <strong>C-suite executive experience</strong> from a nationally recognized United States corporation, combined with hands-on expertise managing <strong>global data center buildouts</strong> for one of the largest companies in the world.
              </p>
              <p className="text-[#7A756E] text-sm leading-loose mb-8">
                From <strong>utility company project management</strong> to complex multi-stakeholder coordination, we bring decades of combined business acumen to every engagement. This isn\u2019t outsourced task work \u2014 it\u2019s strategic partnership from professionals who\u2019ve operated at the executive level and understand the demands you face.
              </p>
              <a href="#contact" className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#C8A97E] text-white text-sm font-medium rounded-md hover:bg-[#B89A6F] transition-colors cursor-pointer whitespace-nowrap">
                Schedule a Consultation <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
            <div className="relative">
              <div className="rounded-xl overflow-hidden shadow-xl">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20executive%20working%20at%20a%20premium%20modern%20desk%20with%20multiple%20monitors%20showing%20business%20dashboards%20and%20calendars%2C%20warm%20ambient%20lighting%2C%20earth%20tone%20office%20interior%20with%20wood%20and%20leather%20accents%2C%20warm%20caramel%20and%20cream%20color%20palette%2C%20sophisticated%20corporate%20environment%2C%20editorial%20business%20photography&width=600&height=500&seq=exec-support-hero&orientation=portrait"
                  alt="Executive Support Services"
                  className="w-full h-[440px] object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-[#2D2A26] rounded-xl p-6 shadow-lg hidden lg:block">
                <p className="text-[#C8A97E] font-serif text-2xl mb-1">C-Suite</p>
                <p className="text-white/70 text-xs tracking-wide">Level<br />Experience</p>
              </div>
            </div>
          </div>

          {/* Service Offerings Grid */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h3 className="font-serif text-3xl text-[#2D2A26] mb-3">Comprehensive Service Offerings</h3>
              <p className="text-[#7A756E] text-sm max-w-lg mx-auto">Every aspect of your professional operations, managed with precision and strategic insight.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                {
                  icon: 'ri-calendar-check-line',
                  title: 'Calendar Optimization & Scheduling',
                  desc: 'Strategic time-blocking, priority scheduling, and seamless coordination across time zones to maximize your productivity.',
                },
                {
                  icon: 'ri-mail-send-line',
                  title: 'Inbox Management',
                  desc: 'Intelligent email triage, response drafting, and communication prioritization to keep you focused on high-impact decisions.',
                },
                {
                  icon: 'ri-flight-takeoff-line',
                  title: 'Travel Planning & Booking',
                  desc: 'End-to-end travel coordination including flights, accommodations, ground transport, and detailed itineraries for domestic and international trips.',
                },
                {
                  icon: 'ri-slideshow-3-line',
                  title: 'Meeting Preparation',
                  desc: 'Comprehensive briefing documents, agenda creation, attendee research, and follow-up action tracking for every meeting.',
                },
                {
                  icon: 'ri-links-line',
                  title: 'Vendor Coordination',
                  desc: 'Managing vendor relationships, negotiating contracts, tracking deliverables, and ensuring seamless service delivery across all providers.',
                },
                {
                  icon: 'ri-money-dollar-circle-line',
                  title: 'Expense Tracking',
                  desc: 'Meticulous expense categorization, receipt management, report generation, and budget monitoring for complete financial visibility.',
                },
                {
                  icon: 'ri-database-2-line',
                  title: 'Data Entry & Database Management',
                  desc: 'Accurate data input, CRM maintenance, contact management, and database optimization to keep your information systems current.',
                },
                {
                  icon: 'ri-file-text-line',
                  title: 'Document Formatting & Organization',
                  desc: 'Professional document creation, formatting to brand standards, template development, and systematic organization of all business materials.',
                },
                {
                  icon: 'ri-folder-cloud-line',
                  title: 'File Management & Cloud Storage',
                  desc: 'Structured file architecture, cloud storage optimization, access management, and systematic organization for instant retrieval.',
                },
                {
                  icon: 'ri-bar-chart-box-line',
                  title: 'Reports & Presentations',
                  desc: 'Data-driven reports, executive summaries, polished presentations, and visual dashboards that communicate your message with clarity.',
                },
                {
                  icon: 'ri-building-line',
                  title: 'Project Management',
                  desc: 'End-to-end project oversight drawing on experience from global data center buildouts and utility infrastructure projects at enterprise scale.',
                },
                {
                  icon: 'ri-settings-4-line',
                  title: 'All Associated Task Types',
                  desc: 'From research and procurement to process optimization and special projects \u2014 any operational task your business demands, handled with executive-level precision.',
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className={`bg-white rounded-xl p-6 border border-[#E8E2D9]/60 hover:shadow-md hover:border-[#C8A97E]/30 transition-all duration-500 group ${execVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#C8A97E]/10 flex items-center justify-center mb-4 group-hover:bg-[#C8A97E]/20 transition-colors">
                    <i className={`${item.icon} text-[#C8A97E] text-lg`}></i>
                  </div>
                  <h4 className="font-serif text-base text-[#2D2A26] mb-3">
                    <a href="#contact" className="hover:text-[#C8A97E] transition-colors cursor-pointer">{item.title}</a>
                  </h4>
                  <p className="text-[#7A756E] text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Experience Highlights */}
          <div className="bg-[#2D2A26] rounded-2xl p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <span className="text-[#C8A97E] text-xs font-medium tracking-[0.3em] uppercase mb-4 block">Why We\u2019re Different</span>
              <h3 className="font-serif text-3xl lg:text-4xl text-white mb-6 leading-tight">
                Real Executive Experience,<br />Not Just Administrative Support
              </h3>
              <p className="text-white/60 text-sm leading-loose mb-8">
                Most assistant services offer task execution. We offer strategic partnership. Our leadership team has sat in the boardroom, managed billion-dollar infrastructure projects, and navigated the complexities of running operations at national scale. That experience informs every decision we make on your behalf.
              </p>
              <a href="#contact" className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#C8A97E] text-white text-sm font-medium rounded-md hover:bg-[#B89A6F] transition-colors cursor-pointer whitespace-nowrap">
                Learn How We Can Help <i className="ri-arrow-right-line"></i>
              </a>
            </div>
            <div className="space-y-5">
              {[
                {
                  icon: 'ri-building-4-line',
                  title: 'C-Suite Corporate Leadership',
                  desc: 'Direct executive experience at a nationally recognized U.S. corporation, bringing boardroom-level strategic thinking to every engagement.',
                },
                {
                  icon: 'ri-global-line',
                  title: 'Global Data Center Buildouts',
                  desc: 'Hands-on management of large-scale data center infrastructure projects for one of the world\u2019s largest companies.',
                },
                {
                  icon: 'ri-flashlight-line',
                  title: 'Utility Project Management',
                  desc: 'Complex utility company project oversight requiring precision coordination across regulatory, engineering, and operational teams.',
                },
                {
                  icon: 'ri-award-line',
                  title: 'Decades of Combined Experience',
                  desc: 'A vast, multidisciplinary business background spanning operations, finance, logistics, and enterprise-level program management.',
                },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start group">
                  <div className="w-10 h-10 rounded-lg bg-[#C8A97E]/15 flex items-center justify-center flex-shrink-0 group-hover:bg-[#C8A97E]/25 transition-colors">
                    <i className={`${item.icon} text-[#C8A97E] text-lg`}></i>
                  </div>
                  <div>
                    <h4 className="text-white text-sm font-medium mb-1">{item.title}</h4>
                    <p className="text-white/50 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-28 px-8 lg:px-16 bg-[#2D2A26] relative overflow-hidden">
        <div className="absolute top-10 right-16 text-[#C8A97E]/5 font-serif text-[20rem] leading-none select-none pointer-events-none">"</div>
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <span className="text-[#C8A97E] text-xs font-medium tracking-[0.3em] uppercase mb-4 block">Testimonials</span>
            <h2 className="font-serif text-4xl lg:text-5xl text-white">What Our Clients Say</h2>
          </div>

          <div className="relative min-h-[240px]">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className={`absolute inset-0 transition-all duration-700 flex flex-col items-center text-center ${i === activeTestimonial ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}
              >
                <p className="text-white/90 text-xl lg:text-2xl font-serif italic leading-relaxed mb-10 max-w-3xl">
                  "{t.quote}"
                </p>
                <div>
                  <p className="text-[#C8A97E] text-base font-medium">{t.name}</p>
                  <p className="text-white/50 text-sm mt-1">{t.title}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center gap-6 mt-12">
            <button
              onClick={() => setActiveTestimonial(prev => (prev - 1 + testimonials.length) % testimonials.length)}
              className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:border-[#C8A97E] hover:bg-[#C8A97E]/10 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-white/70 text-lg"></i>
            </button>
            <span className="text-white/40 text-sm font-medium tabular-nums min-w-[60px] text-center">
              {activeTestimonial + 1} / {testimonials.length}
            </span>
            <button
              onClick={() => setActiveTestimonial(prev => (prev + 1) % testimonials.length)}
              className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:border-[#C8A97E] hover:bg-[#C8A97E]/10 transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-white/70 text-lg"></i>
            </button>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="relative py-28 px-8 lg:px-16">
        <div className="max-w-6xl mx-auto relative rounded-2xl overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=panoramic%20luxury%20resort%20terrace%20overlooking%20ocean%20at%20golden%20sunset%2C%20warm%20amber%20and%20terracotta%20tones%2C%20elegant%20outdoor%20lounge%20furniture%2C%20premium%20lifestyle%20destination%20photography%2C%20cinematic%20wide%20angle%2C%20dreamy%20atmospheric%20golden%20light&width=1200&height=500&seq=cta-banner-v2&orientation=landscape"
            alt="Luxury lifestyle"
            className="w-full h-[400px] object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2D2A26]/80 via-[#2D2A26]/50 to-transparent"></div>
          <div className="absolute inset-0 flex items-center px-10 lg:px-16">
            <div className="max-w-lg">
              <h2 className="font-serif text-3xl lg:text-4xl text-white mb-5 leading-tight">Ready to Reclaim Your Time?</h2>
              <p className="text-white/70 text-sm leading-relaxed mb-8">
                Join over 150 families who trust Full Circle Assist to manage the complexity of their lives with precision and care.
              </p>
              <a href="#contact" className="inline-flex items-center gap-2 px-8 py-4 bg-[#C8A97E] text-white text-sm font-medium rounded-md hover:bg-[#B89A6F] transition-colors cursor-pointer whitespace-nowrap">
                Book Your Consultation <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-28 px-8 lg:px-16 bg-[#F5F0E8]">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <span className="text-[#C8A97E] text-xs font-medium tracking-[0.3em] uppercase mb-4 block">Get in Touch</span>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#2D2A26] mb-6 leading-tight">Begin Your<br />Journey Today</h2>
            <p className="text-[#7A756E] text-sm leading-relaxed mb-10 max-w-md">
              Every engagement begins with a confidential conversation. Share your needs and we\u2019ll design a support system tailored to your life.
            </p>
            <div className="space-y-6">
              {[
                { icon: 'ri-mail-line', label: 'matthew@fullcircleassist.com' },
                { icon: 'ri-phone-line', label: '(650) 464-3760' },
                { icon: 'ri-time-line', label: 'Available 24/7 for existing clients' },
              ].map(item => (
                <div key={item.label} className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#C8A97E]/10 flex items-center justify-center">
                    <i className={`${item.icon} text-[#C8A97E] text-lg`}></i>
                  </div>
                  <span className="text-[#5A5650] text-sm">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl p-8 lg:p-10 border border-[#E8E2D9]/60 shadow-sm">
            <form
              id="consultation_form"
              data-readdy-form
              onSubmit={handleFormSubmit}
              className="space-y-5"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="first_name" className="block text-xs text-[#7A756E] mb-1.5">First Name</label>
                  <input type="text" id="first_name" name="first_name" required className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#E8E2D9] rounded-md text-sm focus:outline-none focus:border-[#C8A97E] transition-colors" />
                </div>
                <div>
                  <label htmlFor="last_name" className="block text-xs text-[#7A756E] mb-1.5">Last Name</label>
                  <input type="text" id="last_name" name="last_name" required className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#E8E2D9] rounded-md text-sm focus:outline-none focus:border-[#C8A97E] transition-colors" />
                </div>
              </div>
              <div>
                <label htmlFor="email" className="block text-xs text-[#7A756E] mb-1.5">Email</label>
                <input type="email" id="email" name="email" required className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#E8E2D9] rounded-md text-sm focus:outline-none focus:border-[#C8A97E] transition-colors" />
              </div>
              <div>
                <label htmlFor="phone" className="block text-xs text-[#7A756E] mb-1.5">Phone</label>
                <input type="tel" id="phone" name="phone" className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#E8E2D9] rounded-md text-sm focus:outline-none focus:border-[#C8A97E] transition-colors" />
              </div>
              <div>
                <label htmlFor="service_interest" className="block text-xs text-[#7A756E] mb-1.5">Service Interest</label>
                <select id="service_interest" name="service_interest" className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#E8E2D9] rounded-md text-sm focus:outline-none focus:border-[#C8A97E] transition-colors cursor-pointer">
                  <option value="">Select a service</option>
                  <option value="Business Operations">Business Operations</option>
                  <option value="Personal Affairs">Personal Affairs</option>
                  <option value="Household Management">Household Management</option>
                  <option value="Travel Coordination">Travel Coordination</option>
                  <option value="Event Planning">Event Planning</option>
                  <option value="Estate Management">Estate Management</option>
                  <option value="Comprehensive Package">Comprehensive Package</option>
                </select>
              </div>
              <div>
                <label htmlFor="message" className="block text-xs text-[#7A756E] mb-1.5">Message</label>
                <textarea id="message" name="message" rows={4} maxLength={500} className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#E8E2D9] rounded-md text-sm focus:outline-none focus:border-[#C8A97E] transition-colors resize-none" placeholder="Tell us about your needs..."></textarea>
                <p className="text-xs text-[#A09A92] mt-1">Max 500 characters</p>
              </div>
              <button type="submit" disabled={formStatus === 'sending'} className="w-full py-3.5 bg-[#C8A97E] text-white text-sm font-medium rounded-md hover:bg-[#B89A6F] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
                {formStatus === 'sending' ? 'Sending...' : 'Submit Inquiry'}
              </button>
              {formStatus === 'success' && <p className="text-emerald-600 text-sm text-center">Thank you! We\u2019ll be in touch shortly.</p>}
              {formStatus === 'error' && <p className="text-red-500 text-sm text-center">Something went wrong. Please try again.</p>}
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#E8E2D9] pt-20 pb-8 px-8 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
            <div className="md:col-span-4">
              <div className="flex items-center gap-2 mb-5">
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="36" height="36" rx="18" fill="#C8A97E"/>
                  <text x="18" y="14" textAnchor="middle" fill="white" fontSize="8" fontFamily="serif" fontWeight="600">F</text>
                  <text x="18" y="21" textAnchor="middle" fill="white" fontSize="8" fontFamily="serif" fontWeight="600">C</text>
                  <text x="18" y="28" textAnchor="middle" fill="white" fontSize="8" fontFamily="serif" fontWeight="600">A</text>
                </svg>
                <span className="text-xl font-serif text-[#2D2A26] tracking-wide">Full Circle <span className="text-[#C8A97E]">Assist</span></span>
              </div>
              <p className="text-[#7A756E] text-sm leading-relaxed mb-6 max-w-xs">
                Complete life infrastructure for high-performing individuals and families. Precision, discretion, excellence.
              </p>
              <div className="flex gap-3">
                {['ri-linkedin-fill', 'ri-instagram-line', 'ri-twitter-x-line'].map((icon, i) => (
                  <a key={i} href="#" rel="nofollow" className="w-9 h-9 rounded-lg bg-[#2D2A26]/10 flex items-center justify-center hover:bg-[#C8A97E] hover:text-white text-[#5A5650] transition-all cursor-pointer">
                    <i className={`${icon} text-base`}></i>
                  </a>
                ))}
              </div>
            </div>

            <div className="md:col-span-2">
              <h4 className="text-[#2D2A26] text-sm font-medium mb-5">
                <a href="#services" className="hover:text-[#C8A97E] transition-colors cursor-pointer">Services</a>
              </h4>
              <ul className="space-y-3">
                {['Business Operations', 'Personal Affairs', 'Household Management', 'Travel Coordination'].map(item => (
                  <li key={item}><a href="#services" className="text-[#7A756E] text-sm hover:text-[#C8A97E] transition-colors cursor-pointer">{item}</a></li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-2">
              <h4 className="text-[#2D2A26] text-sm font-medium mb-5">
                <a href="#about" className="hover:text-[#C8A97E] transition-colors cursor-pointer">Company</a>
              </h4>
              <ul className="space-y-3">
                {[
                  { label: 'About Us', href: '#about' },
                  { label: 'Our Team', href: '/about', isLink: true },
                  { label: 'Testimonials', href: '#testimonials' },
                  { label: 'Contact', href: '#contact' },
                ].map(item => (
                  <li key={item.label}>
                    {'isLink' in item && item.isLink ? (
                      <Link to={item.href} className="text-[#7A756E] text-sm hover:text-[#C8A97E] transition-colors cursor-pointer">{item.label}</Link>
                    ) : (
                      <a href={item.href} className="text-[#7A756E] text-sm hover:text-[#C8A97E] transition-colors cursor-pointer">{item.label}</a>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-4">
              <h4 className="text-[#2D2A26] text-sm font-medium mb-5">Stay Informed</h4>
              <p className="text-[#7A756E] text-sm mb-4">Exclusive insights on life management and premium services.</p>
              <form
                id="newsletter_form"
                data-readdy-form
                onSubmit={handleNewsletter}
                className="flex gap-2"
              >
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="Your email"
                  className="flex-1 px-4 py-2.5 bg-white border border-[#D5CFC5] rounded-md text-sm focus:outline-none focus:border-[#C8A97E] transition-colors"
                />
                <button type="submit" className="px-5 py-2.5 bg-[#C8A97E] text-white text-sm font-medium rounded-md hover:bg-[#B89A6F] transition-colors cursor-pointer whitespace-nowrap">
                  Subscribe
                </button>
              </form>
              {newsletterStatus === 'success' && <p className="text-emerald-600 text-xs mt-2">Subscribed successfully!</p>}
              {newsletterStatus === 'error' && <p className="text-red-500 text-xs mt-2">Please try again.</p>}
            </div>
          </div>

          <div className="border-t border-[#D5CFC5] pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[#A09A92] text-xs">&copy; 2025 Full Circle Assist. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-[#A09A92] text-xs hover:text-[#C8A97E] transition-colors cursor-pointer">Privacy Policy</a>
              <span className="text-[#D5CFC5]">&middot;</span>
              <a href="#" className="text-[#A09A92] text-xs hover:text-[#C8A97E] transition-colors cursor-pointer">Terms of Service</a>
              <span className="text-[#D5CFC5]">&middot;</span>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-[#A09A92] text-xs hover:text-[#C8A97E] transition-colors cursor-pointer">Web Design</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
